import { ChatMessage, Conversation, ConversationRequest, CosmosDBHealth, CosmosDBStatus } from './models'

import { getIdToken, getJwtToken } from './token'

export const historyList = async (offset = 0): Promise<Conversation[] | null> => {
  const response = await fetch(`/api/history/list?offset=${offset}`, {
    method: 'GET',
    headers: {
      Authorization: 'Bearer ' + (await getIdToken()),
      'Content-Type': 'application/json',
    },
  })
    .then(async res => {
      const payload = await res.json()
      if (!Array.isArray(payload)) {
        console.error('There was an issue fetching your data.')
        return null
      }
      const conversations: Conversation[] = await Promise.all(
        payload.map(async (conv: any) => {
          let convMessages: ChatMessage[] = []
          convMessages = await historyRead(conv.id)
            .then(res => {
              return res
            })
            .catch(err => {
              console.error('error fetching messages: ', err)
              return []
            })
          const conversation: Conversation = {
            id: conv.id,
            title: conv.title,
            date: conv.created_at,
            messages: convMessages,
            selected_agent: conv.selected_agent,
          }
          return conversation
        }),
      )
      return conversations
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      return null
    })

  return response
}

export const historyRead = async (convId: string): Promise<ChatMessage[]> => {
  const response = await fetch('/api/history/read', {
    method: 'POST',
    body: JSON.stringify({
      conversation_id: convId,
    }),
    headers: {
      Authorization: 'Bearer ' + (await getIdToken()),
      'Content-Type': 'application/json',
    },
  })
    .then(async res => {
      if (!res) {
        return []
      }
      const payload = await res.json()
      const messages: ChatMessage[] = []
      if (payload?.messages) {
        payload.messages.forEach((msg: any) => {
          const message: ChatMessage = {
            id: msg.id,
            role: msg.role,
            date: msg.created_at,
            content: msg.content,
            feedback: msg.feedback ?? undefined,
            agent_name: msg.agent_name,
          }
          messages.push(message)
        })
      }
      return messages
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      return []
    })
  return response
}

export const historyGenerate = async (
  options: ConversationRequest,
  abortSignal: AbortSignal,
  convId?: string,
): Promise<Response> => {
  let body
  if (convId) {
    body = JSON.stringify({
      conversation_id: convId,
      messages: options.messages,
    })
  } else {
    body = JSON.stringify({
      messages: options.messages,
    })
  }
  const jwtToken = await getJwtToken()
  const response = await fetch('/api/history/generate', {
    method: 'POST',
    headers: {
      Authorization: 'Bearer ' + jwtToken?.idToken,
      'X-MS-Access-Token': jwtToken?.accessToken ?? '',
      'Content-Type': 'application/json',
    },
    body: body,
    signal: abortSignal,
  })
    .then(res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      return new Response()
    })
  return response
}

export const historyUpdate = async (messages: ChatMessage[], convId: string): Promise<Response> => {
  const response = await fetch('/api/history/update', {
    method: 'POST',
    body: JSON.stringify({
      conversation_id: convId,
      messages: messages,
    }),
    headers: {
      Authorization: 'Bearer ' + (await getIdToken()),
      'Content-Type': 'application/json',
    },
  })
    .then(async res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500,
      }
      return errRes
    })
  return response
}

export const historyDelete = async (convId: string): Promise<Response> => {
  const response = await fetch('/api/history/delete', {
    method: 'DELETE',
    body: JSON.stringify({
      conversation_id: convId,
    }),
    headers: {
      Authorization: 'Bearer ' + (await getIdToken()),
      'Content-Type': 'application/json',
    },
  })
    .then(res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500,
      }
      return errRes
    })
  return response
}

export const historyDeleteAll = async (): Promise<Response> => {
  const response = await fetch('/api/history/delete_all', {
    method: 'DELETE',
    body: JSON.stringify({}),
    headers: {
      Authorization: 'Bearer ' + (await getIdToken()),
      'Content-Type': 'application/json',
    },
  })
    .then(res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500,
      }
      return errRes
    })
  return response
}

export const historyRename = async (convId: string, title: string): Promise<Response> => {
  const response = await fetch('/api/history/rename', {
    method: 'POST',
    body: JSON.stringify({
      conversation_id: convId,
      title: title,
    }),
    headers: {
      Authorization: 'Bearer ' + (await getIdToken()),
      'Content-Type': 'application/json',
    },
  })
    .then(res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500,
      }
      return errRes
    })
  return response
}

export const historyEnsure = async (): Promise<CosmosDBHealth> => {
  const response = await fetch('/api/history/ensure', {
    method: 'GET',
    headers: {
      Authorization: 'Bearer ' + (await getIdToken()),
      'Content-Type': 'application/json',
    },
  })
    .then(async res => {
      const respJson = await res.json()
      let formattedResponse
      if (respJson.message) {
        formattedResponse = CosmosDBStatus.Working
      } else {
        if (res.status === 500) {
          formattedResponse = CosmosDBStatus.NotWorking
        } else if (res.status === 401) {
          formattedResponse = CosmosDBStatus.InvalidCredentials
        } else if (res.status === 422) {
          formattedResponse = respJson.error
        } else {
          formattedResponse = CosmosDBStatus.NotConfigured
        }
      }
      if (!res.ok) {
        getJwtToken(true)
        return {
          cosmosDB: false,
          status: formattedResponse,
        }
      } else {
        return {
          cosmosDB: true,
          status: formattedResponse,
        }
      }
    })
    .catch(err => {
      console.error('There was an issue fetching your data.')
      return {
        cosmosDB: false,
        status: err,
      }
    })
  return response
}

export const frontendSettings = async (): Promise<Response | null> => {
  const response = await fetch('/api/frontend_settings', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })
    .then(async res => {
      const jsonResponse = await res.json()
      localStorage.setItem('frontend_settings', JSON.stringify(jsonResponse))
      return jsonResponse
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      return null
    })

  return response
}
export const historyMessageFeedback = async (messageId: string, feedback: string, detailedFeedback: string): Promise<Response> => {
  const response = await fetch('/api/history/message_feedback', {
    method: 'POST',
    body: JSON.stringify({
      message_id: messageId,
      message_feedback: feedback,
      message_detailed_feedback: detailedFeedback,
    }),
    headers: {
      Authorization: 'Bearer ' + (await getIdToken()),
      'Content-Type': 'application/json',
    },
  })
    .then(res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue logging feedback.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500,
      }
      return errRes
    })
  return response
}

export const setAgent = async (selectedAgent: string | null, convId?: string): Promise<Response> => {
  const response = await fetch('/api/conversation/agent', {
    method: 'POST',
    body: JSON.stringify({
      conversation_id: convId,
      selected_agent: selectedAgent,
    }),
    headers: {
      Authorization: 'Bearer ' + (await getIdToken()),
      'Content-Type': 'application/json',
    },
  })
    .then(async res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500,
      }
      return errRes
    })
  return response
}

export const fetchMetrics = async () => {
  const response = await fetch('/api/metrics', {
      method: 'GET',
      headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + (await getIdToken()),
      },
  });
  
  if (!response.ok) {
      throw new Error('Failed to fetch metrics');
  }
  
  return await response.json();
};

export const fetchFeedback = async () => {
  const response = await fetch('/api/feedback', {
      method: 'GET',
      headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + (await getIdToken()),
      },
  });

  if (!response.ok) {
      throw new Error('Failed to fetch feedback');
  }

  return await response.json();
};